import { useMutation } from '@tanstack/react-query';
import API from '../../../../context/API';
import { CreateOptionPayload, UpdateOptionPayload } from '../types';

export function useCreateOption() {
  return useMutation({
    mutationFn: async (data: CreateOptionPayload) => {
      let response;
      if (data.imageFile) {
        const formData = new FormData();
        formData.append('option', data.option);
        formData.append('price', String(parseFloat(String(data.price))));
        formData.append('description', data.description || '');
        formData.append('customization', data.customization.toString());
        formData.append('image', data.imageFile);
        response = await API.post('/options', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const payload: any = {
          option: data.option,
          price: parseFloat(String(data.price)),
          description: data.description || '',
          customization: data.customization.toString(),
        };
        response = await API.post('/options', payload);
      }
      const isSuccess = response.data.success || response.status === 200 || response.status === 201;
      const newOption = response.data.data || response.data;
      if (isSuccess && newOption) return newOption;
      throw new Error(response.data?.message || 'Failed to create the option.');
    },
  });
}

export function useUpdateOption() {
  return useMutation({
    mutationFn: async ({
      id,
      data,
      imageFile,
    }: {
      id: number | string;
      data: UpdateOptionPayload;
      imageFile?: File;
    }) => {
      let response;
      if (imageFile instanceof File) {
        const formData = new FormData();
        if (data.option !== undefined) formData.append('option', data.option);
        if (data.price !== undefined) formData.append('price', String(data.price));
        if (data.description !== undefined) formData.append('description', data.description);
        if (data.sequence_number !== undefined) formData.append('sequence_number', String(data.sequence_number));
        if (data.customization !== undefined) formData.append('customization', String(data.customization));
        formData.append('image', imageFile);
        response = await API.put(`/options/${id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const cleanPayload: any = {};
        if (data.option !== undefined) cleanPayload.option = data.option;
        if (data.price !== undefined) cleanPayload.price = data.price;
        if (data.description !== undefined) cleanPayload.description = data.description;
        if (data.sold_out !== undefined) cleanPayload.sold_out = data.sold_out;
        if (data.sequence_number !== undefined) cleanPayload.sequence_number = Number(data.sequence_number);
        if (data.customization !== undefined) cleanPayload.customization = data.customization;
        if (data.picture !== undefined) cleanPayload.picture = data.picture;
        response = await API.put(`/options/${id}`, cleanPayload);
      }
      if (response.data.success) return response.data.data;
      throw new Error('Failed to update option.');
    },
  });
}

export function useDeleteOption() {
  return useMutation({
    mutationFn: async (id: number | string) => {
      await API.delete(`/options/${id}`);
      return id;
    },
  });
}

export function useBulkUpdateOptionOrder() {
  return useMutation({
    mutationFn: async (updates: { id: number | string; sequence_number: number }[]) => {
      const response = await API.put('/options/bulk-update', { updates });
      if (response.data.success) return updates;
      throw new Error('Failed to reorder options.');
    },
  });
}
